import { Component, input, output, ViewEncapsulation, ChangeDetectionStrategy, signal, computed, inject, LOCALE_ID } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import DataTable from 'datatables.net-dt';
import { BaseComponent } from 'src/app/core/shared/common/base.component';
import { DatatableComponent } from 'src/app/core/components/datatable/datatable.component';
import { MetricCardComponent } from 'src/app/core/components/metric-card/metric-card.component';
import { VssOverviewDataResponse } from '../../../../../../../core/models/analytics/vss-models';
import { SVGIcon, SVGIconModule } from '@progress/kendo-angular-icons';
import { moreVerticalIcon } from '@progress/kendo-svg-icons';
import { environment } from 'src/environments/environment';

/** Plain text cells: DataTables writes raw HTML, and render.text() is its own escaper. */
const text = DataTable.render.text();

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-vulnerability-overview',
  templateUrl: './vulnerability-overview.component.html',
  styleUrls: ['./vulnerability-overview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, ButtonsModule, MetricCardComponent, SVGIconModule, DialogsModule, DatatableComponent],
})
export class VulnerabilityOverviewComponent extends BaseComponent {
  private locale = inject(LOCALE_ID);

  vssOverviewDataResponse = input<VssOverviewDataResponse>(new VssOverviewDataResponse());
  onViewDetails = output<void>();
  protected readonly Math = Math;
  public moreVerticalIcon: SVGIcon = moreVerticalIcon;

  public organizationId = input<number | null>();

  public showPopup = signal<boolean>(false);

  /** No period applies. */
  public datatableUrl = computed((): string => {
    const id = this.organizationId();
    return id != null ? `${environment.apiUrl}/v1/vicarius/analytics/vulnerabilitiesdetails(${id})` : '';
  });

  private date = (value: string) => (value ? formatDate(value, 'MMM d, y, h:mm:ss a', this.locale) : '');

  public datatableColumns = [
    { data: 'endpoint_name', name: 'endpoint_name', title: 'Endpoint | Device Name', type: 'string', render: text },
    { data: 'agent_id', name: 'agent_id', title: 'Endpoint ID', type: 'string', render: text },
    { data: 'patch_name', name: 'patch_name', title: 'Patch', type: 'string', render: text },
    { data: 'patch_id', name: 'patch_id', title: 'Patch ID', type: 'string', render: text },
    { data: 'external_reference', name: 'external_reference', title: 'Reference', type: 'string', render: text },
    { data: 'severity', name: 'severity', title: 'Severity', type: 'string', render: text },
    {
      data: 'is_patchable',
      name: 'is_patchable',
      title: 'Patch Available',
      type: 'string',
      render: (value: unknown) => (value ? 'Yes' : 'No'),
    },
    { data: 'patch_released_at', name: 'patch_released_at', title: 'Patch Released', type: 'string', render: this.date },
    { data: 'last_seen', name: 'last_seen', title: 'Last Seen', type: 'string', render: this.date },
  ];

  onViewData(): void {
    this.showPopup.set(true);
  }

  close(): void {
    this.showPopup.set(false);
  }
}
